import { firestore, auth } from "firebase/app";

import { db } from "@/main";
import { getClientRefById, getClientId } from "./ClientRepository";

import { filterOutUndefined, generateRandomUuid, getLastTimeChanged, getProjectLastOperation } from "@/utils/Utils";
import { getActiveUserId, getRecordInfo } from "@/data/repository/UserRepository";
import { saveAs } from "file-saver";

import {
  ProjectDetails,
  ProjectNameSource,
  ExtendedProjectNameSource,
  isIdOfFlexProject,
  containsDryingInfo,
  ProjectOperationName,
  ApprovalResult,
  FIRST_CONTACT_PERSON_ID,
  isPortalOnlyProject,
  containsOnlyLeakDetectionInfo,
  ProjectOperationSource,
} from "@/data/models/ProjectDetails";

import { ProjectMetaData } from "@/data/models/ProjectMetaData";

import { iresAxiosInstance } from "@/api/IresApi";

import {
  getProjectOverview,
  setProjectOverviewOfFinishedProject,
  deleteProjectOverview,
  setProjectOverview,
  ProjectOverviewOptions,
} from "@/data/repository/ProjectOverviewRepository";
import { deleteProjectStep, saveProjectStep } from "./ProjectStatusRepository";
import Report, { reportStorageLocationToDownloadRef } from "../models/Report";
import { setDamageReportsOutdated, setLeakDetectionReportsOutdated, setProjectReportsOutdated } from "./ReportRepository";
import { DamagePatternConclusionState, DryingProjectOverview, isLeakDetectionOnly } from "../models/DryingProjectOverview";
import { addProjectEventLog } from "./ProjectLogRepository";
import { createProjectEventLog, ProjectEventType } from "../models/ProjectLog";
import { UserModule } from "@/store/modules/UserModule";
import {
  getArchivedProjectRef,
  getProjectCollectionRef,
  getProjectRef,
  isDryingProject,
  isLeakDetectionProject,
} from "./ProjectPaths";

// Path helpers moved to `ProjectPaths` (kept importable from here for existing callers).
export { getProjectRef, isDryingProject, isLeakDetectionProject };

export enum ProjectRetrievalErrorState {
  PROJECT_DOES_NOT_EXIST = "PROJECT_DOES_NOT_EXIST",
  PROJECT_IS_ARCHIVED = "PROJECT_IS_ARCHIVED",
}

export enum NewProjectScope {
  DRYING = "DRYING",
  LEAK_DETECTION = "LEAK_DETECTION",
  FLEX = "FLEX",
}

export enum ProjectType {
  DRYING = "DRYING",
  LEAK_DETECTION = "LEAK_DETECTION",
}

export enum ProjectPhase {
  SET_UP,
  LEAK_DETECTION,
  DAMAGE_ASSESSMENT,
  DRYING,
}

async function setProjectNumberIres(projectId: string) {
  const payload = {
    projectId: projectId,
    clientId: getClientId(),
  };

  const response = await iresAxiosInstance.put(
    `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/setProjectNumberIres`,
    payload
  );

  return response.data.projectNumberIres;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateProject(projectId: string, data: any) {
  const ref = getProjectCollectionRef(projectId) + "/" + projectId;
  return db.doc(ref).update(data);
}

export async function getProjectDetails(
  projectId: string,
  returnArchivedProjectDetails?: boolean,
): Promise<ProjectDetails | ProjectRetrievalErrorState> {
  const ref = getProjectCollectionRef(projectId) + "/" + projectId;

  const projectDetails = (await db.doc(ref).get()).data() as ProjectDetails;

  if (!projectDetails) {
    const archivedProjectRef = getArchivedProjectRef(projectId);
    const archivedProjectDetails = (await db.doc(archivedProjectRef).get()).data() as ProjectDetails;

    if (!archivedProjectDetails) {
      return ProjectRetrievalErrorState.PROJECT_DOES_NOT_EXIST;
    }

    return returnArchivedProjectDetails
      ? archivedProjectDetails
      : ProjectRetrievalErrorState.PROJECT_IS_ARCHIVED;
  }

  if (!projectDetails.clientData.projectNumberIres) {
    const projectNumberIres = await setProjectNumberIres(projectDetails.id);

    if (projectNumberIres) {
      projectDetails.clientData.projectNumberIres = projectNumberIres;
    }
  }

  return projectDetails;
}

/**
 * Reads project details from a specific client's Firestore path.
 * Requires the caller to have cross-client read permission (e.g. IRES ADMIN).
 */
export async function getProjectDetailsOfClient(
  projectId: string,
  clientId: string,
): Promise<ProjectDetails | ProjectRetrievalErrorState> {
  const clientRef = getClientRefById(clientId);
  const projectsCollection = isDryingProject(projectId) || isIdOfFlexProject(projectId)
    ? "projects" : "leakDetectionProjects";

  const ref = `${clientRef}/${projectsCollection}/${projectId}`;
  const projectDetails = (await db.doc(ref).get()).data() as ProjectDetails;

  if (!projectDetails) {
    const archivedRef = `${clientRef}/archivedProjects/${projectId}`;
    const archivedDetails = (await db.doc(archivedRef).get()).data() as ProjectDetails;

    if (!archivedDetails) {
      return ProjectRetrievalErrorState.PROJECT_DOES_NOT_EXIST;
    }

    return archivedDetails;
  }

  return projectDetails;
}

export function createNewProject(
  newProjectScope: NewProjectScope,
  directOrder: boolean
): ProjectDetails {
  const recordInfo = getRecordInfo();
  const randomId = generateRandomUuid();
  const postfix =
    newProjectScope === NewProjectScope.FLEX
      ? "_flex"
      : newProjectScope === NewProjectScope.LEAK_DETECTION
      ? "_ld"
      : "";

  const projectScope =
    newProjectScope === NewProjectScope.FLEX
      ? []
      : newProjectScope === NewProjectScope.LEAK_DETECTION
      ? [ProjectType.LEAK_DETECTION]
      : [ProjectType.DRYING];

  return {
    id: `${randomId}${postfix}`,
    internalProjectNumber: 1,
    firmShort: "",
    clientData: {
      projectApproval: null,
      branch: null,
      projectScope: projectScope,
      contractDate: null,
      directOrder: directOrder,
      firstContactDate: null,
      firstContactTime: null,
      leakDetectionDate: null,
      plannedLeakDetectionDate: null,
      damageDay: null,
      projectStartDate: firestore.Timestamp.fromDate(new Date()),
      policyHolderInfo: {},
      projectName: "",
      projectNumber: "",
      note: "",
      startedBy: recordInfo,
      recordInfo: recordInfo,
      damageNumber: "",
      projectNameSource: ProjectNameSource.USER_INPUT,
      damageLocationInfo: {},
      contactPersons: [
        {
          contactPersonNumber: 1,
          id: FIRST_CONTACT_PERSON_ID,
        },
      ],
      regulatorInfo: {
        regulatorType: null,
      },
    },
  };
}

export enum ProjectSaveResult {
  PROJECT_SAVED,
  PROJECT_SAVE_FAILED,
  PROJECT_NAME_ALREADY_EXISTS,
  PROJECT_NUMBER_ALREADY_EXISTS,
}

export async function doesOtherProjectWithFieldValueExist(
  projectId: string,
  field: string,
  value: string
) {
  const ref = getProjectCollectionRef(projectId);
  const result = await db
    .collection(ref)
    .where(field, "==", value)
    .limit(2)
    .get();

  let doesExist = false;

  result.docs.forEach((doc) => {
    const project = doc.data() as ProjectDetails;

    if (!doesExist && project && project.id !== projectId) {
      doesExist = true;
    }
  });

  return doesExist;
}

export async function doesOtherProjectWithProjectNumberExist(
  projectDetails: ProjectDetails
) {
  return doesOtherProjectWithFieldValueExist(
    projectDetails.id,
    "clientData.projectNumber",
    projectDetails.clientData.projectNumber
  );
}

export async function doesOtherProjectWithProjectNameExist(
  projectDetails: ProjectDetails
) {
  const projectName = projectDetails.clientData.projectName;

  if (!projectName) {
    return false;
  }

  return doesOtherProjectWithFieldValueExist(
    projectDetails.id,
    "clientData.projectName",
    projectName
  );
}

export enum OrderType {
  DIRECT_ORDER = "DIRECT_ORDER",
  INSURANCE_CASE = "INSURANCE_CASE",
}

export async function setProjectOwnership(
  projectDetails: ProjectDetails,
  projectOwnerId: string | null,
  projectSupervisorId: string | null,
  projectOwnerStaffNumber: string | null,
  projectSupervisorStaffNumber: string | null,
  changeMadeBy: string | null,
) {
  const lastTimeChanged = getLastTimeChanged();
  const lastOperation = getProjectLastOperation(
    ProjectOperationName.PROJECT_OWNERS_UPDATED,
    lastTimeChanged.recordedAt
  ); 

  const payload = {
    projectOwnerId: projectOwnerId,
    projectSupervisorId: projectSupervisorId,
    projectOwnerStaffNumber: projectOwnerStaffNumber,
    projectSupervisorStaffNumber: projectSupervisorStaffNumber,
    changeMadeBy: changeMadeBy,
    lastTimeChanged: lastTimeChanged,
    lastOperation: lastOperation,
  };

  try {
    const response = await iresAxiosInstance.put(
      `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/projectOwnership`,
      payload,
      {
        params: {
          projectIdIres: projectDetails.clientData.projectNumberIres,
        },
      }
    );

    if (response.status === 200) {
      const updatedProject = await getProjectDetails(projectDetails.id);

      if (
        updatedProject === ProjectRetrievalErrorState.PROJECT_DOES_NOT_EXIST ||
        updatedProject === ProjectRetrievalErrorState.PROJECT_IS_ARCHIVED
      ) {
        throw Error;
      }

      return {
        result: ProjectSaveResult.PROJECT_SAVED,
        data: updatedProject,
      };
    }

    return {
      result: ProjectSaveResult.PROJECT_SAVED,
      data: projectDetails,
    };
  } catch (error) {
    return {
      result: ProjectSaveResult.PROJECT_SAVE_FAILED,
      data: projectDetails,
    };
  }
}

function getLastOperationName(
  projectDetails: ProjectDetails,
  isNewProject: boolean,
  isApprovalDecisionChanged: boolean,
): ProjectOperationName {
  if (isNewProject) {
    return ProjectOperationName.PROJECT_CREATED_BY_USER;
  }

  if (isApprovalDecisionChanged) {
    return projectDetails.clientData.projectApproval?.approvalResult === ApprovalResult.REJECTED
      ? ProjectOperationName.PROJECT_REJECTED_BY_USER
      : ProjectOperationName.PROJECT_APPROVED_BY_USER;
  }

  return ProjectOperationName.PROJECT_EDITED_BY_USER;
}

/** Checks whether the project name was explicitly set by a user (not auto-calculated). */
function isUserInputProjectName(projectDetails: ProjectDetails): boolean {
  const extended = projectDetails.clientData.extendedProjectNameSource;
  if (extended) {
    return extended === ExtendedProjectNameSource.USER_INPUT;
  }
  return projectDetails.clientData.projectNameSource === ProjectNameSource.USER_INPUT;
}

async function persistDryingProject(
  projectDetails: ProjectDetails,
  isNewProject: boolean,
  isApprovalDecisionChanged?: boolean,
  updateRoomsData?: boolean,
) {
  const orderType = projectDetails.clientData.directOrder
    ? OrderType.DIRECT_ORDER
    : OrderType.INSURANCE_CASE;

  const recordInfo = getRecordInfo();

  const lastTimeChanged = getLastTimeChanged();
  const lastOperation = getProjectLastOperation(
    getLastOperationName(
      projectDetails,
      isNewProject,
      isApprovalDecisionChanged ?? false
    ),
    lastTimeChanged.recordedAt
  );

  const payload = filterOutUndefined({
    visibilityRestrictions: projectDetails.visibilityRestrictions,
    extendedVisibilityRestrictions: projectDetails.extendedVisibilityRestrictions ?? undefined,
    projectScope:
      projectDetails.projectScope ?? projectDetails.clientData.projectScope,
    extendedProjectScope: projectDetails.clientData.extendedProjectScope ?? undefined,
    projectTypeId: projectDetails.clientData.projectTypeId ?? undefined,
    orderType: orderType,
    createdBy: recordInfo.recordedByUserId,
    clientId: auth().currentUser?.uid,
    projectStartDate: projectDetails.clientData.projectStartDate
      .toDate()
      .toISOString(),
    projectId: projectDetails.id,
    branch: projectDetails.clientData.branch,
    costCentres: projectDetails.clientData.costCentres,
    projectName: isUserInputProjectName(projectDetails)
      ? projectDetails.clientData.projectName
      : undefined,
    projectNameSource:
      isUserInputProjectName(projectDetails)
        ? projectDetails.clientData.projectNameSource
        : undefined,
    extendedProjectNameSource:
      isUserInputProjectName(projectDetails)
        ? ExtendedProjectNameSource.USER_INPUT
        : undefined,
    projectNumber: projectDetails.clientData.projectNumber,
    projectNameErp: projectDetails.clientData.projectNameErp ?? undefined,
    policyHolderInfo: projectDetails.clientData.policyHolderInfo,
    regulatorInfo: projectDetails.clientData.regulatorInfo,
    insuranceCaseInfo: filterOutUndefined({
      insuranceName: projectDetails.clientData.insuranceName,
      additionalInsuranceInfo: projectDetails.clientData.additionalInsuranceInfo,
      insuranceContactName: projectDetails.clientData.insuranceContactName,
      insurancePhone: projectDetails.clientData.insurancePhone,
      insuranceMobile: projectDetails.clientData.insuranceMobile,
      insuranceEmail: projectDetails.clientData.insuranceEmail,
      policyNumber: projectDetails.clientData.policyNumber,
      processNumber: projectDetails.clientData.eventNumber,
      damageNumber: projectDetails.clientData.damageNumber,
      damageId: projectDetails.clientData.damageId,
      damageType: projectDetails.clientData.damageType,
      damageDate: projectDetails.clientData.damageDay?.toDate()?.toISOString(),
    }),
    contact: projectDetails.clientData.contact ?? null,
    damageLocationInfo: projectDetails.clientData.damageLocationInfo,
    contactPersons: projectDetails.clientData.contactPersons,
    recordInfo: {
      recordedByUserId: recordInfo.recordedByUserId,
      recordedAt: recordInfo.recordedAt.toDate().toISOString(),
    },
    projectApproval: projectDetails.clientData.projectApproval
      ? {
          recordedByUserId:
            projectDetails.clientData.projectApproval.recordInfo
              .recordedByUserId,
          recordedAt: projectDetails.clientData.projectApproval.recordInfo.recordedAt
            .toDate()
            .toISOString(),
          approvalResult:
            projectDetails.clientData.projectApproval.approvalResult,
        }
      : undefined,
    documentationAndLeakDetectionData: filterOutUndefined({
      completedBy: projectDetails.documentationAndLeakDetectionData?.completedBy
        ? {
            recordedByUserId: projectDetails.documentationAndLeakDetectionData?.completedBy.recordedByUserId,
            recordedAt: projectDetails.documentationAndLeakDetectionData?.completedBy.recordedAt
              .toDate()
              .toISOString(),
          }
        : undefined
    }),
    damagePatternData: filterOutUndefined({
      recordInfo: projectDetails.damagePatternData?.recordInfo
        ? {
            recordedByUserId: projectDetails.damagePatternData?.recordInfo.recordedByUserId,
            recordedAt: projectDetails.damagePatternData?.recordInfo.recordedAt
              .toDate()
              .toISOString(),
          }
        : undefined,
      complete: projectDetails.damagePatternData?.complete,
      rooms: updateRoomsData ? projectDetails.damagePatternData?.rooms : undefined,
    }),
    pdfDocument: projectDetails.pdfDocument ?? null,
    pdfDocument2: projectDetails.pdfDocument2 ?? null,
    lastTimeChanged: lastTimeChanged,
    lastOperation: lastOperation,
  });

  return isNewProject
    ? iresAxiosInstance.post(
        `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/createProject`,
        payload
      )
    : iresAxiosInstance.put(
        `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/projectData`,
        payload,
        {
          params: {
            projectIdIres: projectDetails.clientData.projectNumberIres,
          },
        }
      );
}

export async function downloadReportData(downloadRef: string) {
  const url =
    "https://europe-west1-ires-cloud.cloudfunctions.net/webApi/downloadReport";

  const result = await iresAxiosInstance.get(url, {
    responseType: "blob",
    params: {
      downloadRef: downloadRef,
    },
  });

  const tokens = downloadRef.split("/");
  const fileName = tokens[tokens.length - 1];
  const blob = new Blob([result.data]);

  saveAs(window.URL.createObjectURL(blob), fileName);
}

/**
 * Moves a project to the "Finished" status.
 *
 * @param {string} projectId - The ID of the project to move to finished.
 * @returns {Promise<boolean>} - Returns true if the operation was successful, otherwise false.
 */
export async function moveProjectToFinished(
  projectId: string
) {
  try {
    const payload = {
      clientId: getClientId(),
      projectId: projectId,
      userId: getActiveUserId(),
    };

    const apiEndPoint = `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/moveProjectToFinished`;

    const response = await iresAxiosInstance.put(
      apiEndPoint,
      payload
    );

    return response.status === 200;
  } catch (error) {
    return false;
  }
}

/**
 * Deletes a project. Optionally deletes an archived project.
 *
 * @param {string} projectId - The ID of the project to delete.
 * @param {boolean} [isArchivedProject] - Optional flag indicating if the project is archived.
 * @returns {Promise<boolean>} - Returns true if the operation was successful, otherwise false.
 */
export async function deleteProject(
  projectId: string, 
  isArchivedProject?: boolean
) {
  try {
    const payload = {
      clientId: getClientId(),
      projectId: projectId,
      userId: getActiveUserId(),
    };

    const apiEndPoint = isArchivedProject
      ? `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/deleteArchivedProject`
      : `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/deleteProject`;

    const response = await iresAxiosInstance.put(
      apiEndPoint,
      payload
    );

    return response.status === 200;
  } catch (error) {
    return false;
  }
}

/**
 * Deletes multiple projects. Optionally deletes archived projects.
 *
 * @param {string[]} projectIds - The IDs of the projects to delete.
 * @param {boolean} [areArchivedProjects] - Optional flag indicating if the projects are archived.
 * @returns {Promise<boolean>} - Returns true if the operation was successful, otherwise false.
 */
export async function deleteProjects(
  projectIds: string[], 
  areArchivedProjects?: boolean
) {
  try {
    const payload = {
      clientId: getClientId(),
      projectIds: projectIds,
      userId: getActiveUserId(),
    };

    const apiEndPoint = areArchivedProjects
      ? `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/deleteArchivedProject`
      : `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/deleteProject`;

    const response = await iresAxiosInstance.put(
      apiEndPoint,
      payload
    );

    return response.status === 200;
  } catch (error) {
    return false;
  }
}

/**
 * Permanently deletes a project.
 *
 * @param {string} projectId - The ID of the project to delete permanently.
 * @returns {Promise<boolean>} - Returns true if the operation was successful, otherwise false.
 */
export async function deleteProjectPermanently(
  projectId: string
) {
  try {
    const payload = {
      clientId: getClientId(),
      projectId: projectId,
    };

    const apiEndPoint = `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/deleteProjectPermanently`;

    const response = await iresAxiosInstance.put(
      apiEndPoint,
      payload
    );

    return response.status === 200;
  } catch (error) {
    return false;
  }
}

/**
 * Permanently deletes multiple projects.
 *
 * @param {string[]} projectIds - The IDs of the projects to delete permanently.
 * @returns {Promise<boolean>} - Returns true if the operation was successful, otherwise false.
 */
export async function deleteProjectsPermanently(
  projectIds: string[]
) {
  try {
    const payload = {
      clientId: getClientId(),
      projectIds: projectIds,
    };

    const apiEndPoint = `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/deleteProjectPermanently`;

    const response = await iresAxiosInstance.put(
      apiEndPoint,
      payload
    );

    return response.status === 200;
  } catch (error) {
    return false;
  }
}

/**
 * Restores a deleted project.
 *
 * @param {string} projectId - The ID of the project to restore.
 * @returns {Promise<boolean>} - Returns true if the operation was successful, otherwise false.
 */
export async function restoreProject(projectId: string) {
  try {
    const payload = {
      clientId: getClientId(),
      projectId: projectId,
      userId: getActiveUserId(),
    };

    const response = await iresAxiosInstance.put(
      `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/restoreProject`,
      payload
    );

    return response.status === 200;
  } catch (error) {
    return false;
  }
}

/**
 * Restores multiple deleted projects.
 *
 * @param {string[]} projectIds - The IDs of the projects to restore.
 * @returns {Promise<boolean>} - Returns true if the operation was successful, otherwise false.
 */
export async function restoreProjects(projectIds: string[]) {
  try {
    const payload = {
      clientId: getClientId(),
      projectIds: projectIds,
      userId: getActiveUserId(),
    };

    const response = await iresAxiosInstance.put(
      `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/restoreProject`,
      payload
    );

    return response.status === 200;
  } catch (error) {
    return false;
  }
}

export async function saveProject(
  projectDetails: ProjectDetails,
  isNewProject: boolean,
  isApprovalDecisionChanged?: boolean,
  updateRoomsData?: boolean,
) {
  try {
    const response = await persistDryingProject(projectDetails, isNewProject, isApprovalDecisionChanged, updateRoomsData);

    if (isNewProject && response.status === 201) {
      const updatedProject = await getProjectDetails(projectDetails.id);

      if (
        updatedProject === ProjectRetrievalErrorState.PROJECT_DOES_NOT_EXIST ||
        updatedProject === ProjectRetrievalErrorState.PROJECT_IS_ARCHIVED
      ) {
        throw Error;
      }

      return {
        result: ProjectSaveResult.PROJECT_SAVED,
        data: updatedProject,
      };
    }

    return {
      result: ProjectSaveResult.PROJECT_SAVED,
      data: projectDetails,
    };
  } catch (error) {
    return {
      result: ProjectSaveResult.PROJECT_SAVE_FAILED,
      data: projectDetails,
    };
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function updateProjectMetaData(projectId: string, update: any) {
  return db
    .doc(getProjectCollectionRef(projectId) + "/" + projectId)
    .collection("metadata")
    .doc("data")
    .set(update, { merge: true });
}

export function observeProjectMetaData(
  projectId: string,
  onNext: (projectMetaData: ProjectMetaData) => void
) {
  return db
    .doc(getProjectCollectionRef(projectId) + "/" + projectId)
    .collection("metadata")
    .doc("data")
    .onSnapshot((snapshot) => {
      onNext(snapshot.data() as ProjectMetaData);
    });
}

export function observeProject(
  projectId: string,
  onNext: (projectDetails: ProjectDetails) => void
) {
  return db
    .doc(getProjectCollectionRef(projectId) + "/" + projectId)
    .onSnapshot((snapshot) => {
      const data = snapshot.data() as ProjectDetails;

      if (data) {
        onNext(data);
      }
    });
}

export async function reopenPortalProject(
  projectDetails: ProjectDetails,
  projectOverview: DryingProjectOverview,
  projectEventType: ProjectEventType,
  options?: ProjectOverviewOptions,
) {
  const recordInfo = getRecordInfo();
  const lastTimeChanged = getLastTimeChanged();
  const lastOperation = getProjectLastOperation(
    ProjectOperationName.PROJECT_REOPENED_BY_USER,
    lastTimeChanged.recordedAt
  );
  const conclusion = null;

  projectOverview.endDate = null;
  projectOverview.projectState = null;
  projectOverview.conclusion = conclusion;
  projectOverview.lastOperationAt = lastTimeChanged.recordedAt;
  projectOverview.projectConclusionComplete = false;

  await deleteProjectOverview(projectDetails, options);

  const payload = {
    clientId: getClientId(),
    overviewCollection: "finishedProjectOverviews",
    docId: `page_${Math.floor(projectDetails.internalProjectNumber / 100)}`
  };

  const apiEndPoint = `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/updateProjectOverviewDates`;

  try {
    await iresAxiosInstance.put(
      apiEndPoint,
      payload
    );
  }
  catch (error) {
    // [SKIP] Error
  }

  const projectConclusionData = {
    ...(projectDetails.projectConclusionData ?? {}),
    repairAction: null,
    dryingSuccessful: true,
    startedBy: projectDetails.projectConclusionData?.startedBy ?? recordInfo,
    finishedBy: null,
    projectConclusionDate: null,
    complete: false
  };

  projectDetails.projectConclusionData = projectConclusionData;
  projectDetails.conclusion = conclusion;

  const projectEventLog = createProjectEventLog(
    projectEventType,
    recordInfo
  );

  await Promise.all([
    setProjectOverview(projectDetails, projectOverview),
    deleteProjectStep(projectDetails.id, 18),
    updateProject(projectDetails.id, {
      lastOperation: lastOperation,
      lastTimeChanged: lastTimeChanged,
      projectConclusionData: projectConclusionData,
      conclusion: conclusion,
    }),
    // TODO: No need to add an explicit log, should be derived from the project steps?
    addProjectEventLog(
      projectDetails.id,
      projectEventLog
    )
  ]);

  return projectDetails;
}

export async function reopenDryingProject(
  projectDetails: ProjectDetails,
  projectOverview: DryingProjectOverview,
  projectEventType: ProjectEventType,
  options?: ProjectOverviewOptions
) {
  const recordInfo = getRecordInfo();
  const lastTimeChanged = getLastTimeChanged();
  const lastOperation = getProjectLastOperation(
    ProjectOperationName.PROJECT_REOPENED_BY_USER,
    lastTimeChanged.recordedAt
  );
  const conclusion = null;

  if (projectOverview.dryingProjectSteps?.projectResult) {
    projectOverview.dryingProjectSteps.projectResult = null;
  }
  
  projectOverview.endDate = null;
  projectOverview.projectConclusionComplete = false;
  projectOverview.projectConclusionFinishedBy = null;
  projectOverview.projectState = null;
  projectOverview.conclusion = conclusion;
  projectOverview.lastOperationAt = lastTimeChanged.recordedAt;

  await deleteProjectOverview(projectDetails, options);

  const payload = {
    clientId: getClientId(),
    overviewCollection: "finishedProjectOverviews",
    docId: `page_${Math.floor(projectDetails.internalProjectNumber / 100)}`
  };

  const apiEndPoint = `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/updateProjectOverviewDates`;

  try {
    await iresAxiosInstance.put(
      apiEndPoint,
      payload
    );
  }
  catch (error) {
    // [SKIP] Error
  }

  const projectConclusionData = {
    ...(projectDetails.projectConclusionData ?? {}),
    finishedBy: null,
    didConfirmTimeTrackingInput: false,
    projectConclusionDate: null,
    complete: false,
    startedBy: projectDetails.projectConclusionData?.startedBy
        ?? recordInfo,
    recordInfo: null
  };

  projectDetails.projectConclusionData = projectConclusionData;
  projectDetails.conclusion = conclusion;

  const projectEventLog = createProjectEventLog(
    projectEventType,
    recordInfo
  );

  await Promise.all([
    setProjectOverview(projectDetails, projectOverview),
    setProjectReportsOutdated(projectDetails.id),
    deleteProjectStep(projectDetails.id, 18),
    updateProject(projectDetails.id, {
      lastOperation: lastOperation,
      lastTimeChanged: lastTimeChanged,
      projectConclusionData: projectConclusionData,
      conclusion: conclusion,
    }),
    // TODO: No need to add an explicit log, should be derived from the project steps?
    addProjectEventLog(
      projectDetails.id,
      projectEventLog
    )
  ]);

  return projectDetails;
}

export async function reopenLeakDetectionProject(
  projectDetails: ProjectDetails,
  projectOverview: DryingProjectOverview,
  projectEventType: ProjectEventType,
  options?: ProjectOverviewOptions
) {
  const recordInfo = getRecordInfo();
  const lastTimeChanged = getLastTimeChanged();
  const lastOperation = getProjectLastOperation(
    ProjectOperationName.PROJECT_REOPENED_BY_USER,
    lastTimeChanged.recordedAt
  );
  const conclusion = null;

  // TODO: Verify this
  if (projectOverview.dryingProjectSteps?.damageAssessmentResult) {
    projectOverview.dryingProjectSteps.damageAssessmentResult = null;
  }

  if (projectOverview.dryingProjectSteps?.leakDetectionResult) {
    projectOverview.dryingProjectSteps.leakDetectionResult = null;
  }
  
  projectOverview.damagePatternConclusionComplete = false;
  projectOverview.damagePatternConclusionState = DamagePatternConclusionState.UNDEFINED;
  projectOverview.dryingProgressComplete = null;
  projectOverview.endDate = null;
  projectOverview.projectState = null;
  projectOverview.conclusion = conclusion;
  projectOverview.lastOperationAt = lastTimeChanged.recordedAt;

  await deleteProjectOverview(projectDetails, options);

  const payload = {
    clientId: getClientId(),
    overviewCollection: "finishedProjectOverviews",
    docId: `page_${Math.floor(projectDetails.internalProjectNumber / 100)}`
  };

  const apiEndPoint = `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/updateProjectOverviewDates`;

  try {
    await iresAxiosInstance.put(
      apiEndPoint,
      payload
    );
  }
  catch (error) {
    // [SKIP] Error
  }

  const finishDamagePatternData = {
    ...(projectDetails.finishDamagePatternData ?? {}),
    conclusionState: null,
    damagePatternComplete: false,
    leakDetectionCompletedBy: null,
    leakDetectionCompletedWithActualWorkDone: null,
    completedBy: null,
    concludedBy: null,
    complete: false
  };

  projectDetails.finishDamagePatternData = finishDamagePatternData;
  projectDetails.conclusion = conclusion;

  const projectEventLog = createProjectEventLog(
    projectEventType,
    recordInfo
  );

  await Promise.all([
    setProjectOverview(projectDetails, projectOverview),
    setLeakDetectionReportsOutdated(projectDetails.id),
    // TODO: Verify this
    deleteProjectStep(projectDetails.id, 18),
    updateProject(projectDetails.id, {
      lastOperation: lastOperation,
      lastTimeChanged: lastTimeChanged,
      finishDamagePatternData: finishDamagePatternData,
      conclusion: conclusion,
    }),
    // TODO: No need to add an explicit log, should be derived from the project steps?
    addProjectEventLog(
      projectDetails.id,
      projectEventLog
    )
  ]);

  return projectDetails;
}

export async function reopenDamageAssessmentProject(
  projectDetails: ProjectDetails,
  projectOverview: DryingProjectOverview,
  projectEventType: ProjectEventType,
  options?: ProjectOverviewOptions,
) {
  const recordInfo = getRecordInfo();
  const lastTimeChanged = getLastTimeChanged();
  const lastOperation = getProjectLastOperation(
    ProjectOperationName.PROJECT_REOPENED_BY_USER,
    lastTimeChanged.recordedAt
  );
  const conclusion = null;
  
  projectOverview.damagePatternConclusionComplete = false;
  projectOverview.endDate = null;
  projectOverview.projectState = null;
  projectOverview.conclusion = conclusion;
  projectOverview.lastOperationAt = lastTimeChanged.recordedAt;

  await deleteProjectOverview(projectDetails, options);

  const payload = {
    clientId: getClientId(),
    overviewCollection: "finishedProjectOverviews",
    docId: `page_${Math.floor(projectDetails.internalProjectNumber / 100)}`
  };

  const apiEndPoint = `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/updateProjectOverviewDates`;

  try {
    await iresAxiosInstance.put(
      apiEndPoint,
      payload
    );
  }
  catch (error) {
    // [SKIP] Error
  }

  const finishDamagePatternData = {
    ...(projectDetails.finishDamagePatternData ?? {}),
    lastChangedBy: recordInfo,
    concludedBy: null,
    completedBy: null,
    complete: false,
    conclusionStateLastChangedBy: null,
    damagePatternComplete: false,
  };

  const projectConclusionData = {
    ...(projectDetails.projectConclusionData ?? {}),
    finishedBy: null,
    projectConclusionDate: null
  };

  projectDetails.finishDamagePatternData = finishDamagePatternData;
  projectDetails.projectConclusionData = projectConclusionData;
  projectDetails.conclusion = conclusion;

  const projectEventLog = createProjectEventLog(
    projectEventType,
    recordInfo
  );

  await Promise.all([
    setProjectOverview(projectDetails, projectOverview),
    setDamageReportsOutdated(projectDetails.id),
    updateProject(projectDetails.id, {
      lastOperation: lastOperation,
      lastTimeChanged: lastTimeChanged,
      finishDamagePatternData: finishDamagePatternData,
      projectConclusionData: projectConclusionData,
      conclusion: conclusion,
    }),
    // TODO: No need to add an explicit log, should be derived from the project steps?
    addProjectEventLog(
      projectDetails.id,
      projectEventLog
    )
  ]);

  return projectDetails;
}

export async function reopenRejectedProject(
  projectDetails: ProjectDetails,
  projectOverview: DryingProjectOverview,
  projectEventType: ProjectEventType,
  options?: ProjectOverviewOptions,
) {
  const recordInfo = getRecordInfo();
  const lastTimeChanged = getLastTimeChanged();
  const lastOperation = getProjectLastOperation(
    ProjectOperationName.PROJECT_REOPENED_BY_USER,
    lastTimeChanged.recordedAt
  );

  const projectApprovalData = {
    ...(projectDetails.clientData.projectApproval ?? {}),
    approvalResult: ApprovalResult.PENDING,
    recordInfo: recordInfo,
  };
  const conclusion = null;

  if (projectOverview.dryingProjectSteps) {
    delete projectOverview.dryingProjectSteps.projectApproved;
    projectOverview.dryingProjectSteps["projectAccepted"] = {
      createdBy: recordInfo.recordedByUserId,
      creationTimestamp: recordInfo.recordedAt
        .toDate()
        .toISOString()
    };
  }

  projectOverview.endDate = null;
  projectOverview.projectApproval = projectApprovalData;
  projectOverview.projectState = null;
  projectOverview.conclusion = conclusion;
  projectOverview.lastOperationAt = lastTimeChanged.recordedAt;

  await deleteProjectOverview(projectDetails, options);

  const payload = {
    clientId: getClientId(),
    overviewCollection: "finishedProjectOverviews",
    docId: `page_${Math.floor(projectDetails.internalProjectNumber / 100)}`
  };

  const apiEndPoint = `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/updateProjectOverviewDates`;

  try {
    await iresAxiosInstance.put(
      apiEndPoint,
      payload
    );
  }
  catch (error) {
    // [SKIP] Error
  }

  projectDetails.clientData.projectApproval = projectApprovalData;
  projectDetails.clientData.recordInfo = recordInfo;
  projectDetails.conclusion = conclusion;

  const projectEventLog = createProjectEventLog(
    projectEventType,
    recordInfo
  );

  await Promise.all([
    setProjectOverview(projectDetails, projectOverview),
    deleteProjectStep(projectDetails.id, 2),
    updateProject(projectDetails.id, {
      lastOperation: lastOperation,
      lastTimeChanged: lastTimeChanged,
      clientData: projectDetails.clientData,
      conclusion: conclusion,
    }),
    // TODO: No need to add an explicit log, should be derived from the project steps?
    addProjectEventLog(
      projectDetails.id,
      projectEventLog
    )
  ]);

  return projectDetails;
}

export async function reactivateProject(
  projectDetails: ProjectDetails,
  projectOverview: DryingProjectOverview,
  projectEventType: ProjectEventType,
  options?: ProjectOverviewOptions,
) {
  const recordInfo = getRecordInfo();
  const lastTimeChanged = getLastTimeChanged();
  const lastOperation = getProjectLastOperation(
    ProjectOperationName.PROJECT_REACTIVATED_BY_USER,
    lastTimeChanged.recordedAt
  );
  const conclusion = null;
  
  projectOverview.endDate = null;
  projectOverview.projectState = null;
  projectOverview.conclusion = conclusion;
  projectOverview.lastOperationAt = lastTimeChanged.recordedAt;

  await deleteProjectOverview(projectDetails, options);

  const payload = {
    clientId: getClientId(),
    overviewCollection: "finishedProjectOverviews",
    docId: `page_${Math.floor(projectDetails.internalProjectNumber / 100)}`
  };

  const apiEndPoint = `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/updateProjectOverviewDates`;

  try {
    await iresAxiosInstance.put(
      apiEndPoint,
      payload
    );
  }
  catch (error) {
    // [SKIP] Error
  }

  projectDetails.conclusion = conclusion;

  const projectEventLog = createProjectEventLog(
    projectEventType,
    recordInfo
  );

  await Promise.all([
    setProjectOverview(projectDetails, projectOverview),
    updateProject(projectDetails.id, {
      lastOperation: lastOperation,
      lastTimeChanged: lastTimeChanged,
      conclusion: conclusion,
    }),
    addProjectEventLog(
      projectDetails.id,
      projectEventLog
    )
  ]);

  return projectDetails;
}

export async function reopenProject(
  projectDetails: ProjectDetails,
  options?: ProjectOverviewOptions,
  isProjectReactivation?: boolean
) {
  const projectOverview = await getProjectOverview(projectDetails, options);

  const projectEventType = isProjectReactivation
    ? ProjectEventType.PROJECT_REACTIVATED
    : ProjectEventType.PROJECT_REOPENED;

  const isRejectedProject = projectDetails.clientData.projectApproval?.approvalResult === ApprovalResult.REJECTED;

  if (isRejectedProject) {
    return await reopenRejectedProject(
      projectDetails,
      projectOverview,
      projectEventType,
      options
    );
  }
  else if (isPortalOnlyProject(projectDetails)) {
    return await reopenPortalProject(
      projectDetails,
      projectOverview,
      projectEventType,
      options
    );
  }
  else {
    if (!isProjectReactivation) {
      const isProjectConcluded = projectDetails.projectConclusionData?.complete === true;

      if (isProjectConcluded) {
        return await reopenDryingProject(
          projectDetails,
          projectOverview,
          projectEventType,
          options
        );
      }
      else if (isLeakDetectionOnly(projectOverview)) {
        return await reopenLeakDetectionProject(
          projectDetails,
          projectOverview,
          projectEventType,
          options
        );
      }
      else {
        return await reopenDamageAssessmentProject(
          projectDetails,
          projectOverview,
          projectEventType,
          options
        );
      }
    }
    else if (
      isLeakDetectionOnly(projectOverview) &&
      projectDetails.finishDamagePatternData?.leakDetectionCompletedBy !== null
    ) {
      return await reopenLeakDetectionProject(
        projectDetails,
        projectOverview,
        projectEventType,
        options
      );
    }
    else {
      return await reactivateProject(
        projectDetails,
        projectOverview,
        projectEventType,
        options
      );
    }
  }
}

export async function saveFinishedProject(
  projectDetails: ProjectDetails,
  endDate: firestore.Timestamp,
  report: Report | null,
  hyDryCertificate: Report | null,
) {
  const projectOverview = await getProjectOverview(projectDetails, {
    isProjectFinished: false,
  });

  const lastTimeChanged = getLastTimeChanged();
  const lastOperation = getProjectLastOperation(
    ProjectOperationName.PROJECT_FINISHED_BY_USER,
    lastTimeChanged.recordedAt
  ); 

  projectOverview.endDate = endDate;
  projectOverview.projectConclusionComplete = true;

  const downloadRefExternalPdfReport = report
    ? reportStorageLocationToDownloadRef(report.storageLocation)
    : undefined;
  const downloadRefHyDryCertificate = hyDryCertificate
    ? reportStorageLocationToDownloadRef(
        hyDryCertificate.storageLocation
      )
    : undefined;
  
  const downloadAllRefs: string[] = [];

  if (downloadRefExternalPdfReport) {
    downloadAllRefs.push(downloadRefExternalPdfReport);
  }

  if (downloadRefHyDryCertificate) {
    downloadAllRefs.push(downloadRefHyDryCertificate);
  }

  const projectResultStep = filterOutUndefined({
    createdBy: UserModule.activeUserIdentifier,
    creationTimestamp: new Date().toISOString(),
    projectEndDate: endDate.toDate().toISOString(),
    downloadRefExternalPdfReport: downloadRefExternalPdfReport,
    downloadRefHyDryCertificate: downloadRefHyDryCertificate,
    downloadAllRefs: downloadAllRefs,
    source: ProjectOperationSource.PORTAL
  });

  projectOverview.dryingProjectSteps = {
    ...(projectOverview.dryingProjectSteps ?? {}),
    projectResult: projectResultStep,
  };

  await saveProjectStep(projectDetails, 18, projectResultStep, true);
  await deleteProjectOverview(projectDetails, { isProjectFinished: false });

  await Promise.all([
    setProjectOverviewOfFinishedProject(
      projectDetails,
      endDate,
      projectOverview
    ),
    updateProject(projectDetails.id, {
      lastOperation: lastOperation,
      lastTimeChanged: lastTimeChanged,
      projectConclusionData: projectDetails.projectConclusionData,
    }),
  ]);
}

export function getCurrentProjectPhase(
  projectDetails: ProjectDetails
): ProjectPhase {
  if (isLeakDetectionProject(projectDetails.id)) {
    return ProjectPhase.LEAK_DETECTION;
  } else {
    if (
      !projectDetails.documentationAndLeakDetectionData?.startedBy &&
      !projectDetails.finishDamagePatternData?.startedBy &&
      !projectDetails.damagePatternData?.startedBy
    ) {
      return ProjectPhase.SET_UP;
    } else if (containsOnlyLeakDetectionInfo(projectDetails)) {
      return ProjectPhase.LEAK_DETECTION;
    } else if (containsDryingInfo(projectDetails)) {
      if (projectDetails?.finishDamagePatternData?.complete) {
        return ProjectPhase.DRYING;
      } else {
        return ProjectPhase.DAMAGE_ASSESSMENT;
      }
    } else {
      if (!projectDetails?.finishDamagePatternData?.leakDetectionCompletedBy) {
        return ProjectPhase.LEAK_DETECTION;
      } else if (projectDetails?.finishDamagePatternData?.complete) {
        return ProjectPhase.DRYING;
      } else {
        return ProjectPhase.DAMAGE_ASSESSMENT;
      }
    }
  }
}
